import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Stack,
  Avatar
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SecurityIcon from '@mui/icons-material/Security';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import MedicationIcon from '@mui/icons-material/Medication';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ForumIcon from '@mui/icons-material/Forum';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookDemoModal from '../components/BookDemoModal';
import heroDoctorImg from '../assets/hero_doctor.jpg';
import aboutDoctorImg from '../assets/about_doctor.jpg';
import testimonialDoctorImg from '../assets/testimonial_doctor.jpg';
import apolloLogo from '../assets/apollo_logo.png';
import maxLogo from '../assets/max_logo.png';
import practoLogo from '../assets/practo_logo.png';
import pharmeasyLogo from '../assets/pharmeasy_logo.png';

// Brand colors
const TEAL = '#0B6B6B';
const TEAL_DARK = '#094F4F';
const GREEN = '#25D366';
const NAVY = '#1A2F3A';

// ─── Count Up Animation Component ──────────────────────────────────────
const CountUp = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = React.useState(0);
  const elementRef = React.useRef(null);
  const [hasStarted, setHasStarted] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!hasStarted) return;

    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const currentVal = Math.floor(progress * end);
      setCount(currentVal);
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };
    window.requestAnimationFrame(step);
  }, [hasStarted, end, duration]);

  return (
    <span ref={elementRef}>
      {count.toLocaleString('en-US')}{suffix}
    </span>
  );
};

export default function LandingPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    if (location.state?.scrollTo) {
      const el = document.querySelector(location.state.scrollTo);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  // ─── Feature Strip items ───────────────────────────────────────────────
  const features = [
    { icon: <WhatsAppIcon sx={{ fontSize: 32 }} />, title: 'WhatsApp Automation', desc: 'Send reminders, follow-ups and updates automatically' },
    { icon: <MedicationIcon sx={{ fontSize: 32 }} />, title: 'Medicine Reminders', desc: 'Improve adherence with timely medicine reminders' },
    { icon: <MonitorHeartIcon sx={{ fontSize: 32 }} />, title: 'Vitals Monitoring', desc: 'Collect daily vitals and track patient health' },
    { icon: <NotificationsActiveIcon sx={{ fontSize: 32 }} />, title: 'Emergency Alerts', desc: 'Detect critical values and alert doctors instantly' },
    { icon: <EventAvailableIcon sx={{ fontSize: 32 }} />, title: 'Reduce No-Shows', desc: 'Automated confirmations and smart reminders' },
    { icon: <DashboardIcon sx={{ fontSize: 32 }} />, title: 'All-in-One Platform', desc: 'Manage patients, appointments, prescriptions & more' },
  ];

  // ─── How It Works steps ────────────────────────────────────────────────
  const steps = [
    { icon: <PersonAddRoundedIcon sx={{ fontSize: 28, color: '#1d4ed8' }} />, num: '1', title: 'Add Patient', desc: 'Onboard patients and capture medical information.' },
    { icon: <CalendarMonthRoundedIcon sx={{ fontSize: 28, color: '#2563eb' }} />, num: '2', title: 'Automate Engagement', desc: 'Set up WhatsApp workflows for reminders, follow-ups and check-ins.' },
    { icon: <QuestionAnswerRoundedIcon sx={{ fontSize: 28, color: '#0d9488' }} />, num: '3', title: 'Patient Responds', desc: 'Patients receive messages and respond directly on WhatsApp.' },
    { icon: <BarChartRoundedIcon sx={{ fontSize: 28, color: '#0d9488' }} />, num: '4', title: 'Get Insights & Alerts', desc: 'Track adherence, vitals and get alerts for critical conditions.' },
    { icon: <MonitorHeartRoundedIcon sx={{ fontSize: 28, color: '#ef4444' }} />, num: '5', title: 'Provide Better Care', desc: 'Take action early and improve patient outcomes.' },
  ];

  // ─── Stat chips ────────────────────────────────────────────────────────
  const stats = [
    { icon: <PeopleIcon sx={{ fontSize: 32, color: '#0B6B6B' }} />, end: 25000, suffix: '+', label: 'Doctors Trust Us' },
    { icon: <GroupsIcon sx={{ fontSize: 32, color: '#0B6B6B' }} />, end: 3, suffix: 'M+', label: 'Patients Engaged' },
    { icon: <ForumIcon sx={{ fontSize: 32, color: '#0B6B6B' }} />, end: 10, suffix: 'M+', label: 'Messages Sent' },
    { icon: <HealthAndSafetyIcon sx={{ fontSize: 32, color: '#0B6B6B' }} />, end: 98, suffix: '%', label: 'Satisfaction Rate' },
  ];

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      <Navbar onBookDemo={() => setDemoOpen(true)} />

      {/* ─────────────────── HERO ─────────────────── */}
      <Box
        id="home"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 'auto', md: '84vh' },
          display: 'flex',
          alignItems: 'center',
          pt: { xs: 0, sm: 0, md: '72px' },
          pb: { xs: 0, md: 0 },
          backgroundColor: '#0B1929',
        }}
      >
        {/* ── Desktop absolute background Doctor image (entirely unchanged for desktop) ── */}
        <Box
          component="img"
          src={heroDoctorImg}
          alt="Doctor using Care Beyond"
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '62%',
            objectFit: 'cover',
            objectPosition: 'center top',
            zIndex: 0,
            opacity: 1,
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.2) 15%, black 35%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.2) 15%, black 35%)',
          }}
        />

        {/* Subtle teal glow on left */}
        <Box sx={{ position: 'absolute', top: '-100px', left: '-100px', width: 450, height: 450, borderRadius: '50%', background: 'radial-gradient(circle, rgba(11,107,107,0.16) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 1 }} />

        {/* Full-width row */}
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          minHeight: { xs: 'auto', md: '84vh' },
          position: 'relative',
          zIndex: 2
        }}>
          {/* ── Mobile Doctor image — stacks on top, visible on mobile only ── */}
          <Box
            component="img"
            src={heroDoctorImg}
            alt="Doctor using Care Beyond"
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'relative',
              width: '100%',
              height: { xs: '260px', sm: '360px' },
              objectFit: 'cover',
              objectPosition: 'center top',
              zIndex: 1,
              maskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
            }}
          />

          {/* ── LEFT: Text Content ── */}
          <Box sx={{
            flex: { md: '0 0 48%' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-start', md: 'center' },
            px: { xs: 2.5, sm: 4.5, md: 8, lg: 12 },
            pt: { xs: 4, sm: 5, md: 4 },
            pb: { xs: 6, sm: 8, md: 12 },
            position: 'relative',
            zIndex: 3,
          }}>
            {/* WhatsApp badge */}
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.25)', borderRadius: '50px', px: 2, py: 0.7, mb: 3.5, width: 'fit-content' }}>
              <WhatsAppIcon sx={{ color: GREEN, fontSize: 18 }} />
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>WhatsApp-Powered Patient Engagement</Typography>
            </Box>

            {/* Headline */}
            <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: { xs: '34px', sm: '46px', md: '56px', lg: '62px' }, color: '#ffffff', lineHeight: 1.05, letterSpacing: { xs: '-1.2px', md: '-2px' }, mb: 0.5 }}>
              Better Care.
            </Typography>
            <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: { xs: '34px', sm: '46px', md: '56px', lg: '62px' }, color: '#ffffff', lineHeight: 1.05, letterSpacing: { xs: '-1.2px', md: '-2px' }, mb: 0.5 }}>
              Stronger Connections.
            </Typography>
            <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: { xs: '34px', sm: '46px', md: '56px', lg: '62px' }, color: '#25D366', lineHeight: 1.05, letterSpacing: { xs: '-1.2px', md: '-2px' }, mb: 3.5 }}>
              Beyond Consultations.
            </Typography>

            {/* Description */}
            <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 400, fontSize: { xs: '15px', md: '16px' }, color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, mb: 4.5, maxWidth: '420px' }}>
              Care Beyond helps doctors and clinics automate patient engagement, improve treatment adherence, monitor vitals, and detect emergencies — all through WhatsApp.
            </Typography>

            {/* CTA Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1.5, sm: 2 }}
              sx={{ mb: { xs: 3.5, md: 4.5 }, width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="contained"
                onClick={() => setDemoOpen(true)}
                sx={{
                  backgroundColor: TEAL, fontFamily: 'Plus Jakarta Sans', fontWeight: 700,
                  fontSize: '15px',
                  px: 4,
                  py: 1.6,
                  width: { xs: '100%', sm: 'auto' },
                  borderRadius: '10px',
                  textTransform: 'none',
                  boxShadow: '0 8px 28px rgba(11,107,107,0.5)',
                  '&:hover': { backgroundColor: TEAL_DARK, transform: 'translateY(-1px)', boxShadow: '0 12px 32px rgba(11,107,107,0.6)' },
                  transition: 'all 0.2s ease'
                }}
              >
                Get Started for Free
              </Button>
              <Button
                variant="outlined"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                sx={{
                  borderColor: 'rgba(255,255,255,0.35)', color: '#ffffff',
                  fontFamily: 'Plus Jakarta Sans', fontWeight: 700,
                  fontSize: '15px',
                  px: 4,
                  py: 1.6,
                  width: { xs: '100%', sm: 'auto' },
                  borderRadius: '10px',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.07)' }
                }}
              >
                Explore Features
              </Button>
            </Stack>

            {/* Trust badges */}
            <Stack
              direction="row"
              spacing={{ xs: 1.5, sm: 3 }}
              flexWrap="wrap"
              gap={{ xs: 1, sm: 1.5 }}
              sx={{ maxWidth: { xs: '330px', sm: '100%' } }}
            >
              {[
                { icon: <CreditCardOffIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }} />, text: 'No Credit Card Required' },
                { icon: <SecurityIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }} />, text: 'HIPAA Ready' },
                { icon: <LockIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }} />, text: 'Secure & Compliant' },
              ].map((t, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  {t.icon}
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.5)' }}>{t.text}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* ── RIGHT: floating stat cards offset to match mockup (image is bg) ── */}
          <Box sx={{
            flex: { md: '0 0 52%' },
            position: 'relative',
            display: { xs: 'none', md: 'block' },
            zIndex: 5,
            transform: {
              md: 'scale(0.8) translateY(-25px)',
              lg: 'scale(0.9) translateY(-30px)',
              xl: 'scale(1) translateY(-35px)'
            },
            transformOrigin: 'left top'
          }}>
            {/* Card 1: Today's Appointments (Top Left - Received feel) */}
            <Box sx={{
              position: 'absolute',
              top: '6%',
              left: '16%',
              width: '185px',
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
              p: 2.2,
              border: '1px solid rgba(255,255,255,0.8)'
            }}>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '10px', fontWeight: 700, color: '#64748b', mb: 0.5 }}>Today's Appointments</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '32px', color: '#1e293b', lineHeight: 1 }}>24</Typography>
                <CalendarMonthIcon sx={{ color: '#2563eb', fontSize: 28 }} />
              </Box>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '10px', color: '#10b981', fontWeight: 700, mt: 0.8 }}>+12% vs yesterday</Typography>
            </Box>

            {/* Card 2: Medicine Adherence (Middle Right - Sent feel) */}
            <Box sx={{
              position: 'absolute',
              top: '28%',
              left: '42%',
              width: '200px',
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
              p: 2.2,
              border: '1px solid rgba(255,255,255,0.8)'
            }}>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '10px', fontWeight: 700, color: '#64748b', mb: 0.5 }}>Medicine Adherence</Typography>
              <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '32px', color: '#1e293b', lineHeight: 1 }}>82%</Typography>
              <Box sx={{ height: 6, backgroundColor: '#e2e8f0', borderRadius: 3, mt: 1.2, mb: 0.8 }}>
                <Box sx={{ height: 6, width: '82%', backgroundColor: '#10b981', borderRadius: 3 }} />
              </Box>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '10px', color: '#10b981', fontWeight: 700 }}>+8% vs last week</Typography>
            </Box>

            {/* Card 3: Abnormal Vitals (Lower Left - Received feel) */}
            <Box sx={{
              position: 'absolute',
              top: '44%',
              left: '5%',
              width: '185px',
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
              p: 2.2,
              border: '1px solid rgba(255,255,255,0.8)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '10px', fontWeight: 700, color: '#64748b' }}>Abnormal Vitals</Typography>
                <MonitorHeartIcon sx={{ color: '#ef4444', fontSize: 26 }} />
              </Box>
              <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '32px', color: '#1e293b', lineHeight: 1 }}>5</Typography>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '10px', color: '#64748b', fontWeight: 700, mt: 0.8 }}>Needs Attention</Typography>
            </Box>

            {/* Floating WhatsApp Logo (Right of Card 3) */}
            <Box sx={{
              position: 'absolute',
              top: '48%',
              left: '42%',
              backgroundColor: '#ffffff',
              borderRadius: '50%',
              width: 48,
              height: 48,
              boxShadow: '0 8px 24px rgba(37,211,102,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #25D366'
            }}>
              <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
            </Box>

            {/* Card 4: WhatsApp Message Bubble (Bottom Right - Sent feel) */}
            <Box sx={{
              position: 'absolute',
              top: '66%',
              left: '28%',
              width: '290px',
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 12px 36px rgba(0,0,0,0.2)',
              p: 2,
              border: '1px solid rgba(255,255,255,0.8)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#0B6B6B', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ color: '#fff', fontSize: '14px', fontWeight: 800 }}>+</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '11px', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>Care Beyond</Typography>
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '9px', color: '#64748b' }}>Appointment Reminder</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '11px', color: '#334155', lineHeight: 1.5, mb: 0.5 }}>
                Hi Ravi, you have an appointment tomorrow at 10:00 AM.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.2 }}>
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '9px', color: '#94a3b8' }}>10:00 AM</Typography>
                <Typography sx={{ color: '#22c55e', fontSize: '12px', fontWeight: 700 }}>✓✓</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ─────────────────── FEATURES STRIP ─────────────────── */}
      <Box id="features" sx={{
        backgroundColor: 'transparent',
        pt: { xs: 5, md: 0 },
        pb: { xs: 4, md: 6 },
        mt: { xs: 0, md: '-80px' },
        position: 'relative',
        zIndex: 10
      }}>
        <Container sx={{ px: { xs: 1.5, sm: 3, md: 6 }, maxWidth: '1320px !important' }}>
          <Box sx={{
            backgroundColor: NAVY,
            borderRadius: { xs: '18px', md: '24px' },
            boxShadow: '0 20px 48px rgba(11,107,107,0.18)',
            overflow: 'hidden'
          }}>
            <Grid container spacing={0}>
              {features.map((f, i) => (
                <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
                  <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    textAlign: 'center',
                    px: { xs: 1.2, sm: 2.2 },
                    py: { xs: 2.5, sm: 4.5 },
                    borderRight: {
                      xs: i % 2 === 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      sm: i % 3 !== 2 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      md: i < features.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none'
                    },
                    borderBottom: {
                      xs: i < 4 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      sm: i < 3 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      md: 'none'
                    }
                  }}>
                    <Box sx={{ color: '#4DD9D9', mb: 2 }}>{f.icon}</Box>
                    <Typography sx={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: { xs: '12px', sm: '14.5px' }, color: '#ffffff', mb: 0.8, lineHeight: 1.3 }}>{f.title}</Typography>
                    <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 400, fontSize: { xs: '10px', sm: '12px' }, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>{f.desc}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ─────────────────── ABOUT SECTION ─────────────────── */}
      <Box id="about" sx={{ pt: { xs: 4, md: 5 }, pb: { xs: 8, md: 10 }, backgroundColor: '#ffffff' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 } }}>
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={aboutDoctorImg}
                alt="Doctor at clinic"
                sx={{
                  width: '100%',
                  borderRadius: { xs: '16px', md: '20px' },
                  objectFit: 'cover',
                  display: 'block',
                  minHeight: { xs: '280px', sm: '380px', md: 'auto' },
                  maxHeight: { xs: '420px', md: 'none' },
                  boxShadow: '0 20px 48px rgba(11,107,107,0.12)'
                }}
              />
            </Grid>

            {/* Right: content */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: '12px', color: TEAL, letterSpacing: '2px', textTransform: 'uppercase', mb: 2 }}>
                About Care Beyond
              </Typography>
              <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: { xs: '30px', sm: '36px', md: '44px' }, color: NAVY, lineHeight: 1.15, letterSpacing: '-1.5px', mb: 3 }}>
                Empowering Doctors.<br />
                <Box component="span" sx={{ color: TEAL }}>Engaging Patients.</Box>
              </Typography>
              <Stack spacing={2.5} sx={{ mb: 5, maxWidth: '520px' }}>
                {[
                  'Smart patient engagement platform built specifically for doctors and clinics.',
                  'Combines the power of WhatsApp automation with intelligent healthcare workflows.',
                  'Improves patient outcomes, treatment adherence, and saves valuable clinical time.'
                ].map((point, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(37,211,102,0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.3
                    }}>
                      <CheckIcon sx={{ color: '#25D366', fontSize: 14, fontWeight: 900 }} />
                    </Box>
                    <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 500, fontSize: '15px', color: '#475569', lineHeight: 1.5 }}>
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* Stats row */}
              <Grid container spacing={{ xs: 2.5, sm: 3 }} sx={{ mt: 2 }}>
                {stats.map((s, i) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={i}>
                    <Box sx={{
                      textAlign: { xs: 'center', sm: 'left' },
                      pl: { xs: 0, sm: i > 0 ? 4 : 0 },
                      borderRight: { xs: 'none', sm: i < stats.length - 1 ? '1px solid #e2e8f0' : 'none' }
                    }}>
                      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                        {s.icon}
                      </Box>
                      <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '28px', color: TEAL, lineHeight: 1.2 }}>
                        <CountUp end={s.end} suffix={s.suffix} />
                      </Typography>
                      <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 600, fontSize: '11px', color: '#64748b', mt: 0.5 }}>{s.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─────────────────── HOW IT WORKS ─────────────────── */}
      <Box id="how-it-works" sx={{ py: { xs: 5, sm: 5.5, md: 5 }, backgroundColor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
            <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: '11px', color: TEAL, letterSpacing: '2px', textTransform: 'uppercase', mb: 1 }}>
              HOW IT WORKS
            </Typography>
            <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: { xs: '26px', md: '36px' }, color: NAVY, letterSpacing: '-1.5px' }}>
              Simple Steps. Better Outcomes.
            </Typography>
          </Box>

          {/* Desktop-only view: Flex row container (completely unchanged layout for desktop) */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'row',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            width: '100%',
            gap: 0
          }}>
            {steps.map((step, i) => (
              <React.Fragment key={i}>
                {/* Step Card */}
                <Box sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: '20px',
                  p: 2.5,
                  textAlign: 'center',
                  flex: 1,
                  boxShadow: '0 8px 24px rgba(15,35,55,0.02)',
                  border: '1px solid #f1f5f9',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '175px',
                  maxWidth: '215px'
                }}>
                  {/* Icon */}
                  <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {step.icon}
                  </Box>
                  {/* Title */}
                  <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '14px', color: NAVY, mb: 0.5 }}>
                    {step.num}. {step.title}
                  </Typography>
                  {/* Desc */}
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 500, fontSize: '11px', color: '#64748b', lineHeight: 1.45 }}>
                    {step.desc}
                  </Typography>
                </Box>

                {/* Arrow between steps */}
                {i < steps.length - 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1 }}>
                    <ArrowForwardIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* Mobile/Tablet-only view: Centered Grid container */}
          <Grid container spacing={{ xs: 2.5, sm: 3 }} justifyContent="center" sx={{ display: { xs: 'flex', md: 'none' } }}>
            {steps.map((step, i) => (
              <Grid
                size={{ xs: 12, sm: 6 }}
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                {/* Step Card */}
                <Box sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: '20px',
                  p: 2.5,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: '145px', sm: '160px' },
                  width: '100%',
                  maxWidth: '100%',
                  boxShadow: '0 8px 24px rgba(15,35,55,0.02)',
                  border: '1px solid #f1f5f9',
                }}>
                  {/* Icon */}
                  <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {step.icon}
                  </Box>
                  {/* Title */}
                  <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '14px', color: NAVY, mb: 0.5 }}>
                    {step.num}. {step.title}
                  </Typography>
                  {/* Desc */}
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 500, fontSize: '11px', color: '#64748b', lineHeight: 1.45 }}>
                    {step.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─────────────────── TESTIMONIAL & PARTNERS ─────────────────── */}
      <Box sx={{ py: { xs: 5, md: 5.5 }, backgroundColor: '#091E26', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 } }}>
          <Grid container spacing={4} alignItems="center">
            {/* Left Portion: Testimonial (Quote + Doctor Info) */}
            <Grid size={{ xs: 12, md: 7.2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 2.5, md: 3.5 } }}>
                {/* Quote block */}
                <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                  <FormatQuoteIcon sx={{ fontSize: 32, color: TEAL, mt: -0.5, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: { xs: '14px', md: '15px' }, color: '#cbd5e1', lineHeight: 1.6, fontStyle: 'italic' }}>
                    "Care Beyond has transformed the way I manage my patients. The WhatsApp automation and real-time alerts help me provide better care every day."
                  </Typography>
                </Box>
                {/* Doctor info block with vertical/horizontal responsive divider */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.8,
                  flexShrink: 0,
                  borderRight: { xs: 'none', md: '1px solid rgba(255,255,255,0.15)' },
                  borderBottom: { xs: '1px solid rgba(255,255,255,0.1)', md: 'none' },
                  pr: { xs: 0, md: 4.5 },
                  pb: { xs: 3, md: 0 },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Avatar
                    src={testimonialDoctorImg}
                    sx={{ width: 50, height: 50, border: `2px solid ${TEAL}` }}
                  />
                  <Box sx={{ minWidth: '120px' }}>
                    <Typography sx={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: '14px', color: '#ffffff', lineHeight: 1.2 }}>Dr. Neha Sharma</Typography>
                    <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontSize: '11.5px', color: '#94a3b8', mb: 0.3 }}>Cardiologist</Typography>
                    <Stack direction="row" spacing={0.3}>
                      {[...Array(5)].map((_, i) => <StarIcon key={i} sx={{ fontSize: 11, color: '#f59e0b' }} />)}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Portion: Partners Logos */}
            <Grid size={{ xs: 12, md: 4.8 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, pl: { md: 2 } }}>
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: '10px', color: '#94a3b8', mb: 2, textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Trusted by Leading Doctors & Clinics
                </Typography>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'space-between' },
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  width: '100%',
                  gap: { xs: 2, sm: 4 }
                }}>
                  {/* Apollo */}
                  <Box
                    component="img"
                    src={apolloLogo}
                    alt="Apollo Hospitals"
                    sx={{ height: 56, opacity: 0.9, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                  />
                  {/* Max */}
                  <Box
                    component="img"
                    src={maxLogo}
                    alt="MAX Healthcare"
                    sx={{ height: 52, opacity: 0.9, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                  />
                  {/* Practo */}
                  <Box
                    component="img"
                    src={practoLogo}
                    alt="Practo"
                    sx={{ height: 32, opacity: 0.9, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                  />
                  {/* PharmEasy */}
                  <Box
                    component="img"
                    src={pharmeasyLogo}
                    alt="PharmEasy"
                    sx={{ height: 32, opacity: 0.9, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─────────────────── FOOTER ─────────────────── */}
      <Footer onOpenDemo={() => setDemoOpen(true)} />
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </Box>
  );
}